/**
 * PHARMAKON — Reset Password (Phase 1)
 *
 * Public page reached from the emailed link (/reset-password?token=...). Sets a
 * new password via POST /auth/reset-password, then redirects to /login. Visual
 * style matches the login page.
 */
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Wordmark,
  Field,
  Input,
  Button,
  Icon,
} from "@/components/ui/primitives";
import { authApi } from "@/services/index.js";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = pwd.length > 0 && pwd.length < 8;
  const mismatch = confirm.length > 0 && pwd !== confirm;
  const canSubmit = token && pwd.length >= 8 && pwd === confirm && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await authApi.resetPassword(token, pwd);
      setDone(true);
      // Brief confirmation, then back to login.
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          "Lien de réinitialisation invalide ou expiré. Veuillez en demander un nouveau.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen brand-canvas-bg flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Wordmark size="lg" />
        </div>

        <div className="bg-surface border border-border-subtle rounded-xl shadow-pop p-8">
          {done ? (
            <div className="text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-success-soft text-success flex items-center justify-center">
                <Icon name="check-circle" size={26} />
              </div>
              <h1 className="text-xl font-semibold text-text-strong mb-2">
                Mot de passe réinitialisé
              </h1>
              <p className="text-sm text-text-muted mb-6">
                Votre mot de passe a été mis à jour. Redirection vers la
                connexion…
              </p>
              <Button
                variant="primary"
                size="md"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Se connecter
              </Button>
            </div>
          ) : !token ? (
            <div className="text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-danger-soft text-danger flex items-center justify-center">
                <Icon name="alert-triangle" size={26} />
              </div>
              <h1 className="text-xl font-semibold text-text-strong mb-2">
                Lien invalide
              </h1>
              <p className="text-sm text-text-muted mb-6">
                Ce lien de réinitialisation est incomplet ou a expiré. Veuillez
                en demander un nouveau.
              </p>
              <Button
                variant="primary"
                size="md"
                className="w-full"
                onClick={() => navigate("/forgot-password")}
              >
                Demander un nouveau lien
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-text-strong mb-1.5">
                Nouveau mot de passe
              </h1>
              <p className="text-sm text-text-muted mb-8">
                Choisissez un nouveau mot de passe sécurisé pour votre compte.
              </p>

              {error && (
                <div className="mb-5 flex items-start gap-2.5 px-3.5 py-2.5 bg-danger-soft border border-danger/30 rounded-md">
                  <Icon
                    name="alert-circle"
                    size={14}
                    className="text-danger mt-0.5 shrink-0"
                  />
                  <div className="text-xs text-danger">{error}</div>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <Field
                  label="Nouveau mot de passe"
                  required
                  error={tooShort ? "Au moins 8 caractères." : undefined}
                >
                  <div className="relative">
                    <Input
                      type={showPwd ? "text" : "password"}
                      name="new-password"
                      autoComplete="new-password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      icon="lock"
                      placeholder="••••••••"
                      size="lg"
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={
                        showPwd
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-default transition-colors"
                    >
                      <Icon name={showPwd ? "eye-off" : "eye"} size={15} />
                    </button>
                  </div>
                </Field>

                <Field
                  label="Confirmer le mot de passe"
                  required
                  error={
                    mismatch ? "Les mots de passe ne correspondent pas." : undefined
                  }
                >
                  <Input
                    type={showPwd ? "text" : "password"}
                    name="confirm-password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    icon="lock"
                    placeholder="••••••••"
                    size="lg"
                    required
                  />
                </Field>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={loading}
                  disabled={!canSubmit}
                >
                  Réinitialiser le mot de passe
                </Button>
              </form>
            </>
          )}
        </div>

        {!done && (
          <p className="text-center text-sm text-text-muted mt-6">
            <Link
              to="/login"
              className="text-accent hover:underline inline-flex items-center gap-1.5"
            >
              <Icon name="arrow-left" size={14} />
              Retour à la connexion
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
